/**
 * "Are this day's orders final?" for the cafeteria surface.
 *
 * The Serve print flow can print days ahead of service, so it has to say which
 * of those days can still change. That answer already exists for the
 * school-admin ordering surfaces — `orderClosedReason` wrapping `@ordo/recurrence`
 * `computeOrderDeadline` — and this module is only the adapter that feeds it a
 * cafeteria location wire.
 *
 * Side-effect free; unit-tested in `client/test/lib/cafeteria/order-window.test.ts`.
 */

import type { CafeteriaStaffLocationWire } from '@/api/cafeteria'
import { orderClosedReason, type SchoolDeadlinePolicy } from '@/lib/school-orders/deadline'

/**
 * Pick the deadline policy off a cafeteria location wire, or null when the
 * campus's policy didn't arrive.
 *
 * The policy fields are optional on the wire (only the service-session
 * endpoints populate them), and `computeOrderDeadline` THROWS on a malformed
 * `orderDeadlineTime` — so a partial policy must never be hand-assembled into
 * an `OrderDeadlinePolicy`. Callers get null and fall back to "not confirmed",
 * which shows the warning rather than silently claiming a day is final.
 *
 * `orderDeadlineDayOfWeek` and `orderDeadlineRelativeDays` are legitimately
 * null (a campus with no weekly deadline; a campus not on RELATIVE_DAYS), so
 * only the three non-nullable columns gate the result.
 */
export function cafeteriaDeadlinePolicy(
  location: Pick<
    CafeteriaStaffLocationWire,
    | 'timeZone'
    | 'orderDeadlineMode'
    | 'orderDeadlineDayOfWeek'
    | 'orderDeadlineWeeksBefore'
    | 'orderDeadlineTime'
    | 'orderDeadlineRelativeDays'
  > | null,
): SchoolDeadlinePolicy | null {
  if (
    !location ||
    location.orderDeadlineMode == null ||
    location.orderDeadlineWeeksBefore == null ||
    location.orderDeadlineTime == null ||
    !location.timeZone
  ) {
    return null
  }
  return {
    orderDeadlineMode: location.orderDeadlineMode,
    orderDeadlineDayOfWeek: location.orderDeadlineDayOfWeek ?? null,
    orderDeadlineWeeksBefore: location.orderDeadlineWeeksBefore,
    orderDeadlineTime: location.orderDeadlineTime,
    orderDeadlineRelativeDays: location.orderDeadlineRelativeDays ?? null,
    timeZone: location.timeZone,
  }
}

/**
 * Whether `iso`'s orders are final — the ordering window has closed, so what
 * prints is what gets served. A day already past counts as final; a day whose
 * cutoff hasn't arrived does not.
 *
 * With no resolvable policy this returns false (NOT final), so an unprovisioned
 * or partially-loaded campus warns instead of over-promising.
 */
export function ordersConfirmedForDay(
  iso: string,
  policy: SchoolDeadlinePolicy | null,
  today?: string,
  now?: Date,
): boolean {
  if (!policy) {
    return false
  }
  return orderClosedReason(iso, policy, today, now) !== null
}
